import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';

/**
 * Account entry point for the top-right control rail.
 * - Logged out: opens the login/sign-up modal.
 * - Logged in: toggles the account panel (the "account menu").
 * Visually matches the rail buttons in MapControls.
 */
export function AccountButton() {
  const user = useAuthStore((s) => s.user);
  const openAuthModal = useUIStore((s) => s.openAuthModal);
  const togglePanel = useUIStore((s) => s.togglePanel);
  const accountPanelOpen = useUIStore((s) => s.panels.accountPanel);

  const loggedIn = !!user;
  const label = loggedIn ? 'Account' : 'Sign in';

  const handleClick = () => {
    if (loggedIn) {
      togglePanel('accountPanel');
    } else {
      openAuthModal('login');
    }
  };

  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <button
      onClick={handleClick}
      title={label}
      aria-label={label}
      aria-pressed={accountPanelOpen}
      className={`group relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
        accountPanelOpen ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {loggedIn ? (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
          {initial}
        </span>
      ) : (
        <AccountIcon className="h-5 w-5" />
      )}
      {user?.account_type === 'business' && (
        <span
          aria-hidden
          className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-secondary-500 ring-2 ring-white"
        />
      )}
      <span className="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {label}
      </span>
    </button>
  );
}

function AccountIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}
